import React from 'react';
import { captureException } from '@sentry/browser';
import Modal from '@aha-app/react-bootstrap-modal';
import Turbolinks from 'javascripts/turbolinks';

/*
 * Generic ErrorBoundary component which can be used to wrap
 * any React component; shows a sorry Fred message when it
 * catches an error (rather than the default, which is usually
 * a blank screen).
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  componentDidCatch(error, info) {
    this.setState({ hasError: true });
    captureException(error, { extra: info });
  }

  errorPanel() {
    return (
      <div className='error-container'>
        <img
          src='/questionmark-error.svg'
          width='280'
          alt='Aha! error symbol'
        />
        <div className='error__message'>
          <h1>Something went wrong</h1>
          <p>
            We have been notified. Feel free to contact us
            <br /> and we will let you know when it is fixed.
          </p>
          <p>
            <button
              className='btn btn-link'
              onClick={() => Turbolinks.visit(location.href)}
            >
              Reload
            </button>{' '}
            or <a href='mailto:support@aha.io'>contact us</a>.
          </p>
        </div>
      </div>
    );
  }

  render() {
    if (this.state.hasError) {
      if (this.props.modal) {
        return (
          <>
            <Modal.Header closeButton />
            <Modal.Body>{this.errorPanel()}</Modal.Body>
          </>
        );
      } else {
        return this.errorPanel();
      }
    }
    return this.props.children;
  }
}

/**
 * HOC that wraps the given component with an ErrorBoundary. Useful
 * for rendering an ErrorBoundary outside the top level of a
 * component.
 *
 * @template {React.ComponentType<any>} T
 * @param {T} Component
 * @returns {React.FC<React.ComponentProps<T>>}
 */
export const withErrorBoundary = Component => {
  const ErrorBoundaryWrapper = props => (
    <ErrorBoundary>
      <Component {...props} />
    </ErrorBoundary>
  );
  return ErrorBoundaryWrapper;
};
